import React, { useState, useEffect, useRef } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PlaylistToolbar from './PlaylistToolbar';
import TimeGrid from './TimeGrid';
// Heritage theme colors embedded

// Playlist color palette (lighter versions for better visibility)
const PLAYLIST_COLORS = [
    '#a8c5e0', // navy light
    '#7fb8b8', // teal light
    '#7fb89f', // emerald light
    '#e6c84d', // goldLight bright
    '#d4a5d4', // סגול בהיר
    '#f5b57a', // כתום בהיר
    '#6dd4b8', // ירוק ים בהיר
    '#f08080', // אדום בהיר
    '#87ceeb', // כחול בהיר
    '#dda0dd', // סגול כהה בהיר
];

// Get playlist color
const getPlaylistColor = (playlistId, allPlaylists) => {
    const playlist = allPlaylists.find(p => p.playlistId === playlistId);
    if (playlist?.color) return playlist.color;

    const index = allPlaylists.findIndex(p => p.playlistId === playlistId);
    return index === -1 ? PLAYLIST_COLORS[0] : PLAYLIST_COLORS[index % PLAYLIST_COLORS.length];
};

/**
 * ScheduleEditor - Custom weekly schedule editor with paint logic
 * No external calendar libraries - pure CSS Grid implementation
 */
const ScheduleEditor = ({ open, onClose, schedule, playlists, onSave }) => {
    const [slots, setSlots] = useState({});
    const [activePlaylist, setActivePlaylist] = useState(null);
    const [isPainting, setIsPainting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0, visible: false });
    const [isHoveringDeleteBtn, setIsHoveringDeleteBtn] = useState(false);

    // Resize state
    const isResizing = useRef(false);
    const resizingPlaylistId = useRef(null);

    // Initialize slots from schedule or create default
    useEffect(() => {
        if (open && playlists.length > 0) {
            // רק אם אין slots קיימים (פתיחה ראשונה) או אם ה-schedule השתנה מבחוץ
            const hasExistingSlots = Object.keys(slots).length > 0;

            if (!hasExistingSlots) {
                const initialSlots = schedule && (Array.isArray(schedule) ? schedule.length > 0 : Object.keys(schedule).length > 0)
                    ? scheduleToSlots(schedule)
                    : createDefaultSlots(playlists[0]);

                setSlots(initialSlots);

                // בחירת הפלייליסט הפעיל הראשון - מעדיף מפורסם
                const firstPublishedPlaylist = playlists.find(p => p.isPublished !== false);
                setActivePlaylist(firstPublishedPlaylist || playlists[0]);

                setValidationError(null);
            }
        }

        // איפוס כשהפופ-אפ נסגר
        if (!open) {
            setSlots({});
            setActivePlaylist(null);
            setValidationError(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, playlists]);

    // Convert schedule object to slots
    const scheduleToSlots = (schedule) => {
        const newSlots = {};

        // תמיכה במבנה החדש (אובייקט) והישן (מערך)
        if (Array.isArray(schedule)) {
            // מבנה ישן: schedule הוא מערך
            schedule.forEach(entry => {
                const startHour = parseInt(entry.startTime.split(':')[0]);
                let endHour = parseInt(entry.endTime.split(':')[0]);
                const endMinute = parseInt(entry.endTime.split(':')[1]);

                // If endTime is 23:59, treat as 24:00
                if (endHour === 23 && endMinute === 59) {
                    endHour = 24;
                }

                entry.days.forEach(day => {
                    for (let hour = startHour; hour < endHour; hour++) {
                        const key = `${day}-${hour}`;
                        const playlist = playlists.find(p => p.playlistId === entry.playlistId);
                        const color = getPlaylistColor(entry.playlistId, playlists);
                        newSlots[key] = {
                            playlistId: entry.playlistId,
                            name: playlist?.name || 'מערך תצוגה',
                            color: color
                        };
                    }
                });
            });
        } else {
            // מבנה חדש: schedule הוא אובייקט
            for (let day = 0; day < 7; day++) {
                const daySchedule = schedule[day] || [];

                daySchedule.forEach(entry => {
                    const startHour = parseInt(entry.startTime.split(':')[0]);
                    let endHour = parseInt(entry.endTime.split(':')[0]);
                    const endMinute = parseInt(entry.endTime.split(':')[1]);

                    // If endTime is 23:59, treat as 24:00
                    if (endHour === 23 && endMinute === 59) {
                        endHour = 24;
                    }

                    for (let hour = startHour; hour < endHour; hour++) {
                        const key = `${day}-${hour}`;
                        const playlist = playlists.find(p => p.playlistId === entry.playlistId);
                        const color = getPlaylistColor(entry.playlistId, playlists);
                        newSlots[key] = {
                            playlistId: entry.playlistId,
                            name: playlist?.name || 'מערך תצוגה',
                            color: color
                        };
                    }
                });
            }
        }

        return newSlots;
    };

    // Create default schedule (all slots filled with first playlist)
    const createDefaultSlots = (defaultPlaylist) => {
        const newSlots = {};
        const color = getPlaylistColor(defaultPlaylist.playlistId, playlists);

        for (let day = 0; day < 7; day++) {
            for (let hour = 0; hour < 24; hour++) {
                const key = `${day}-${hour}`;
                newSlots[key] = {
                    playlistId: defaultPlaylist.playlistId,
                    name: defaultPlaylist.name,
                    color: color
                };
            }
        }

        return newSlots;
    };

    // Convert slots back to schedule format (new structure)
    const slotsToSchedule = () => {
        const schedule = {};

        // קבץ לפי יום
        for (let day = 0; day < 7; day++) {
            const daySlots = [];

            for (let hour = 0; hour < 24; hour++) {
                const key = `${day}-${hour}`;
                const slot = slots[key];

                if (slot) {
                    daySlots.push({
                        hour,
                        playlistId: slot.playlistId
                    });
                }
            }

            // המר לפורמט schedule עם קיבוץ שעות רצופות
            const daySchedule = [];

            if (daySlots.length > 0) {
                let currentBlock = {
                    playlistId: daySlots[0].playlistId,
                    startHour: daySlots[0].hour,
                    endHour: daySlots[0].hour + 1
                };

                for (let i = 1; i < daySlots.length; i++) {
                    const slot = daySlots[i];

                    // אם אותו פלייליסט ושעה רצופה - הרחב את הבלוק
                    if (slot.playlistId === currentBlock.playlistId &&
                        slot.hour === currentBlock.endHour) {
                        currentBlock.endHour = slot.hour + 1;
                    } else {
                        // שמור את הבלוק הנוכחי והתחל חדש
                        daySchedule.push({
                            playlistId: currentBlock.playlistId,
                            startTime: `${String(currentBlock.startHour).padStart(2, '0')}:00`,
                            endTime: currentBlock.endHour === 24 ? '23:59' : `${String(currentBlock.endHour).padStart(2, '0')}:00`
                        });

                        currentBlock = {
                            playlistId: slot.playlistId,
                            startHour: slot.hour,
                            endHour: slot.hour + 1
                        };
                    }
                }

                // שמור את הבלוק האחרון של היום
                daySchedule.push({
                    playlistId: currentBlock.playlistId,
                    startTime: `${String(currentBlock.startHour).padStart(2, '0')}:00`,
                    endTime: currentBlock.endHour === 24 ? '23:59' : `${String(currentBlock.endHour).padStart(2, '0')}:00`
                });
            }

            schedule[day] = daySchedule;
        }

        return schedule;
    };

    // Paint slot with active playlist
    const paintSlot = (day, hour) => {
        if (!activePlaylist) return;

        // מניעת צביעה עם פלייליסט לא מפורסם
        if (activePlaylist.isPublished === false) return;

        const key = `${day}-${hour}`;
        const color = getPlaylistColor(activePlaylist.playlistId, playlists);

        setSlots(prev => ({
            ...prev,
            [key]: {
                playlistId: activePlaylist.playlistId,
                name: activePlaylist.name,
                color: color
            }
        }));
    };

    // Paint slot with specific playlist (for resizing)
    const paintSlotWithPlaylist = (day, hour, playlistId) => {
        const playlist = playlists.find(p => p.playlistId === playlistId);
        if (!playlist) return;

        const key = `${day}-${hour}`;
        const color = getPlaylistColor(playlistId, playlists);

        setSlots(prev => ({
            ...prev,
            [key]: {
                playlistId: playlist.playlistId,
                name: playlist.name,
                color: color
            }
        }));
    };

    // Mouse handlers for paint logic
    const handleMouseDown = (day, hour) => {
        if (isResizing.current) return; // Don't paint while resizing
        setIsPainting(true);
        paintSlot(day, hour);
    };

    const handleMouseEnter = (day, hour) => {
        if (isResizing.current && resizingPlaylistId.current) {
            // Mode B: Resizing
            paintSlotWithPlaylist(day, hour, resizingPlaylistId.current);
        } else if (isPainting) {
            // Mode A: Regular painting
            paintSlot(day, hour);
        }
    };

    const handleMouseUp = () => {
        setIsPainting(false);
        isResizing.current = false;
        resizingPlaylistId.current = null;
    };

    // Track cursor position for custom cursor
    const handleMouseMove = (e) => {
        setCursorPosition({
            x: e.clientX,
            y: e.clientY,
            visible: true
        });
    };

    const handleMouseLeave = () => {
        setCursorPosition(prev => ({ ...prev, visible: false }));
    };

    // Handle resize start
    const handleResizeStart = (playlistId) => {
        isResizing.current = true;
        resizingPlaylistId.current = playlistId;
    };

    // Handle remove playlist from time range
    const handleRemovePlaylist = (day, startHour, endHour, playlistId) => {
        // מחיקת כל השעות בטווח הזה
        setSlots(prev => {
            const newSlots = { ...prev };
            for (let hour = startHour; hour < endHour; hour++) {
                const key = `${day}-${hour}`;
                if (newSlots[key]?.playlistId === playlistId) {
                    delete newSlots[key];
                }
            }
            return newSlots;
        });
    };

    // Validate that all 168 slots are filled
    const validateSchedule = () => {
        const totalSlots = 7 * 24; // 168 slots
        const filledSlots = Object.keys(slots).length;

        if (filledSlots !== totalSlots) {
            return {
                valid: false,
                message: `לוח הזמנים חייב להיות מלא! חסרות ${totalSlots - filledSlots} שעות.`
            };
        }

        // Check that all slots have a valid playlist
        for (let day = 0; day < 7; day++) {
            for (let hour = 0; hour < 24; hour++) {
                const key = `${day}-${hour}`;
                const slot = slots[key];

                if (!slot || !slot.playlistId) {
                    return {
                        valid: false,
                        message: `שעה ריקה נמצאה ביום ${day + 1} בשעה ${String(hour).padStart(2, '0')}:00`
                    };
                }
            }
        }

        return { valid: true };
    };

    // Save schedule
    const handleSave = async () => {
        try {
            console.log('🔵 ScheduleEditor: Starting save...');
            setIsSaving(true);
            setValidationError(null);

            // Validate schedule
            const validation = validateSchedule();
            if (!validation.valid) {
                setValidationError(validation.message);
                setIsSaving(false);
                return;
            }

            const scheduleData = slotsToSchedule();
            console.log('🔵 ScheduleEditor: Calling onSave...');
            await onSave(scheduleData);
            console.log('🔵 ScheduleEditor: onSave completed!');

            // סגירת הפופ-אפ רק אחרי שהשמירה הצליחה
            setIsSaving(false);
            console.log('🔵 ScheduleEditor: Closing dialog...');
            onClose();
        } catch (error) {
            console.error('🔴 ScheduleEditor: Error saving schedule:', error);
            setValidationError('שגיאה בשמירת לוח הזמנים');
            setIsSaving(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '90vh',
                        maxWidth: '95vw',
                        width: '95vw',
                        m: 2,
                        direction: 'rtl',
                        borderRadius: '12px',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        bgcolor: '#1e3a5f',
                        color: 'white',
                        py: 1,
                        px: 3
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 24 }} />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                    עריכת לוח זמנים
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                                    לחץ על מערך תצוגה וצבע את השעות בלוח
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            onClick={onClose}
                            disabled={isSaving}
                            size="small"
                            sx={{
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent
                    sx={{
                        p: 2.5,
                        bgcolor: '#f8fafc',
                        position: 'relative'
                    }}
                >
                    {validationError && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: '8px',
                                border: '1px solid #fecaca',
                                bgcolor: '#fef2f2'
                            }}
                            onClose={() => setValidationError(null)}
                        >
                            {validationError}
                        </Alert>
                    )}

                    <PlaylistToolbar
                        playlists={playlists}
                        activePlaylist={activePlaylist}
                        onSelectPlaylist={setActivePlaylist}
                    />

                    <Box
                        sx={{
                            position: 'relative',
                            '& .time-grid-container': {
                                cursor: 'none' // הסתרת הסמן רק בלוח
                            },
                            '& .time-grid-slot:hover .remove-playlist-btn': {
                                cursor: 'pointer' // סמן רגיל על כפתור המחיקה
                            }
                        }}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                    >
                        <TimeGrid
                            slots={slots}
                            onMouseDown={handleMouseDown}
                            onMouseEnter={handleMouseEnter}
                            onMouseUp={handleMouseUp}
                            onResizeStart={handleResizeStart}
                            onRemovePlaylist={handleRemovePlaylist}
                            onDeleteBtnHover={setIsHoveringDeleteBtn}
                        />
                    </Box>

                    {/* Custom Cursor - כפתור מערך התצוגה הפעיל */}
                    {activePlaylist && cursorPosition.visible && !isHoveringDeleteBtn && (
                        <Box
                            sx={{
                                position: 'fixed',
                                left: cursorPosition.x,
                                top: cursorPosition.y,
                                transform: 'translate(-50%, -50%)',
                                pointerEvents: 'none',
                                zIndex: 9999,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1,
                                py: 0.5,
                                borderRadius: '6px',
                                bgcolor: getPlaylistColor(activePlaylist.playlistId, playlists),
                                border: '1.5px solid white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                transition: 'left 0.05s ease, top 0.05s ease'
                            }}
                        >
                            {/* Color Dot */}
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: 'white',
                                    flexShrink: 0
                                }}
                            />

                            {/* Playlist Name */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: 'white',
                                    fontSize: '0.7rem',
                                    whiteSpace: 'nowrap',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                }}
                            >
                                {activePlaylist.name}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 2,
                        gap: 2,
                        bgcolor: '#ffffff',
                        borderTop: '1px solid #e2e8f0'
                    }}
                >
                    <Box sx={{ marginRight: 'auto', display: 'flex', gap: 2 }}>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={isSaving}
                            sx={{
                                bgcolor: '#1e3a5f',
                                px: 3,
                                py: 1,
                                borderRadius: '8px',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                textTransform: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: '#2d5a8f',
                                    boxShadow: 'none'
                                },
                                '&:disabled': {
                                    bgcolor: '#cbd5e1',
                                    color: '#94a3b8'
                                }
                            }}
                        >
                            {isSaving ? 'שומר...' : 'שמור'}
                        </Button>
                        <Button
                            onClick={onClose}
                            disabled={isSaving}
                            variant="text"
                            sx={{
                                px: 3,
                                py: 1,
                                borderRadius: '8px',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                textTransform: 'none',
                                color: '#64748b',
                                '&:hover': {
                                    bgcolor: '#f1f5f9'
                                }
                            }}
                        >
                            ביטול
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ScheduleEditor;
